import * as Yup from 'yup';
import { App } from '../../App';
import { AppRegistrationSection } from '../../AppRegistrationSection';
import {
  ButtonActor,
  CheckboxActor,
  DateSelector,
  FieldGroup,
  InputActor,
  Spacer,
  Subtitle,
  Title,
} from 'components';
import { format } from 'date-fns';
import { Frame } from 'components/organisms';
import { frameConfig } from '../../AppConfig';
import { getScreenTranslation } from '../../utils/translations';
import { JUICY_NEXT_BTN } from 'utils/constants';
import { personNameRegExp } from '../../utils/regularExpressions';
import { routes } from '../../routes';
import { useErrorManager } from 'InternalLibrary';
import { useFormik } from 'formik';
import { useMaybeCreateApplication } from '../../hooks/useMaybeCreateApplication';
import { useNavigate } from 'react-router-dom';
import { usePutProfile } from '../../api/atoms';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from 'classnames';
import IdentifyEvent from '../../hooks/eventCollector/events/IdentifyEvent';
import layout from 'scss/layout.module.scss';
import styles from './PersonalData.module.scss';
import useClient from '../../hooks/useClient';
import useClientConstraints from '../../hooks/useClientConstraints';
import useClientOffer from '../../hooks/useClientOffer';
import useEventCollector from '../../hooks/eventCollector/useEventCollector';

export default function PersonalData() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setError } = useErrorManager();
  const { fetch: fetchPutProfile } = usePutProfile();
  const { fetchClient } = useClient();
  const { data: dataOffer, fetch: fetchOffer } = useClientOffer();
  const { maybeCreateApplication } = useMaybeCreateApplication();
  const { identify } = useEventCollector();
  const i18next = useTranslation();
  const { fetchClientConstraints } = useClientConstraints();

  const validationSchema = Yup.object().shape({
    noSecondSurnameCheckbox: Yup.boolean(),
    firstName: Yup.string()
      .min(2, i18next.t('validation:wrong_format'))
      .required(i18next.t('validation:required_field'))
      .matches(personNameRegExp, i18next.t('validation:wrong_format')),
    firstSurname: Yup.string()
      .min(2, i18next.t('validation:wrong_format'))
      .required(i18next.t('validation:required_field'))
      .matches(personNameRegExp, i18next.t('validation:wrong_format')),
    secondSurname: Yup.string().when(
      'noSecondSurnameCheckbox',
      (checkboxChecked, schema) => {
        return checkboxChecked
          ? schema
          : schema
              .min(2, i18next.t('validation:wrong_format'))
              .required(i18next.t('validation:required_field'))
              .matches(personNameRegExp, i18next.t('validation:wrong_format'));
      },
    ),
    birthdate: Yup.date()
      .typeError(i18next.t('validation:required_field'))
      .required(i18next.t('validation:required_field')),
  });

  const onSubmit = async (data: any) => {
    if (!data.birthdate) {
      // This shouldn't happen. Ensured by Yup
      return;
    }
    try {
      await fetchPutProfile({
        body: {
          firstName: data.firstName,
          lastName: `${data.firstSurname}${
            data.noSecondSurnameCheckbox ? '' : ` ${data.secondSurname}`
          }`,
          dateOfBirth: format(data.birthdate, 'yyyy-MM-dd'),
          registrationPhase: 'PERSONAL_DATA',
        },
      });

      const client = await fetchClient();

      const { creditLimit, dateOfBirth } = client || {};

      identify(
        new IdentifyEvent({
          client,
          email: client?.email?.email || 'undefined',
        }),
      );

      const creditLimitExceeded = !!(
        dataOffer?.newPrincipal &&
        dateOfBirth &&
        creditLimit &&
        creditLimit < dataOffer.newPrincipal
      );

      const errorParam = creditLimitExceeded ? '?age-error=true' : '';
      const nextRoute = `${routes.FirstLoan_Address}${errorParam}`;

      await fetchClientConstraints({
        queryParameters: {
          amount: String(creditLimit),
          term: String(dataOffer?.term),
        },
        client,
      });
      if (creditLimitExceeded) {
        await fetchOffer({
          queryParameters: {
            amount: creditLimit,
            term: App.config.term || dataOffer?.term!,
          },
          client,
        });
        await maybeCreateApplication({ amount: creditLimit, nextRoute });
      } else {
        await fetchOffer();
        await maybeCreateApplication({ nextRoute });
      }

      navigate(nextRoute);
    } catch (error) {
      // TODO: show form field error instead of error notification
      setError(error);
    }
  };

  const formik = useFormik({
    initialValues: {
      firstName: '',
      firstSurname: '',
      secondSurname: '',
      noSecondSurnameCheckbox: false,
      birthdate: null,
    },
    validationSchema,
    onSubmit: async (data) => {
      formik.setStatus({ errors: {} });
      setLoading(true);
      await onSubmit(data);
      setLoading(false);
    },
  });

  const content = getScreenTranslation('personalData', i18next);

  return (
    <Frame {...frameConfig} sectionID={AppRegistrationSection.Confirmation}>
      <form onSubmit={formik.handleSubmit}>
        <div className={layout['width-sm']}>
          <Spacer marginBottom="30px">
            <Title text={content.title} data-test="css-Personal-Data-Title" />
          </Spacer>

          <Spacer marginBottom="30px" className={styles['subtitle-wrapper']}>
            <Subtitle
              heading={content.subtitleHeading}
              text={content.subtitleText}
              headingClassName={styles['subtitle-heading']}
              textClassName={styles['subtitle-text']}
              inline={false}
              dataTest="css-Important-Info"
            />
          </Spacer>

          <Spacer marginBottom="20px">
            <FieldGroup direction="vertical">
              <InputActor
                label={content.firstName}
                name="firstName"
                formik={formik}
                data-test="id-FirstName"
                fieldGroupProps={{
                  dataTestValidationText: 'firstNameValidationText',
                }}
                inputProps={{
                  dataTest: 'firstNameInput',
                }}
                analytics={{
                  description: 'First name(s)',
                }}
              />
              <InputActor
                className={styles.border}
                label={content.firstSurname}
                name="firstSurname"
                formik={formik}
                data-test="id-FirstSurname"
                fieldGroupProps={{
                  dataTestValidationText: 'firstSurnameValidationText',
                }}
                inputProps={{
                  dataTest: 'firstSurnameInput',
                }}
                analytics={{
                  description: 'Last name',
                }}
              />
              {!formik.values.noSecondSurnameCheckbox && (
                <InputActor
                  className={styles['border-end']}
                  label={content.secondSurname}
                  name="secondSurname"
                  formik={formik}
                  data-test="id-SecondSurname"
                  inputProps={{
                    dataTest: 'secondSurnameInput',
                  }}
                  fieldGroupProps={{
                    dataTestValidationText: 'secondSurnameValidationText',
                  }}
                  analytics={{
                    description: 'Second last name',
                  }}
                />
              )}
            </FieldGroup>
          </Spacer>

          <Spacer marginBottom="30px">
            <CheckboxActor
              formik={formik}
              name="noSecondSurnameCheckbox"
              textHTML={content.surnameCheckbox}
              checkboxProps={{
                className: styles['check-box'],
                highlight: true,
                dataTest: 'noSecondSurnameCheckbox',
                disabled: loading,
              }}
              dataTest="css-Checkbox-Not-Have-SecondSurname"
              analytics={{
                description: 'No second lastname checkbox',
              }}
            />
          </Spacer>

          <Spacer marginBottom={{ default: '70px', m: '50px' }}>
            <DateSelector
              correctInvalidDays
              onChange={(date: Date | null) => {
                formik.setFieldValue('birthdate', date);
              }}
              fieldGroupProps={{
                labelText: content.birthdateFieldGroupLabelText,
                validationText:
                  formik.touched.birthdate &&
                  typeof formik.errors['birthdate'] === 'string'
                    ? formik.errors['birthdate']
                    : undefined,
                dataTestValidationText: 'dateSelectorValidationText',
              }}
              inputClassName={styles['birthdate-picker-selector']}
              ageRange={{ max: 123, min: 2 }}
              analytics={{
                description: 'Date of birth',
              }}
            />
          </Spacer>

          <Spacer marginBottom={{ s: '40px' }}>
            <ButtonActor
              buttonProps={{
                className: classes([styles['continue-button'], JUICY_NEXT_BTN]),
                type: 'submit',
                loading,
                dataTest: 'css-Continue-Button',
              }}
              formik={formik}
              analytics={{
                description: 'Continue button',
              }}
            >
              {content.continueButtonText}
            </ButtonActor>
          </Spacer>
        </div>
      </form>
    </Frame>
  );
}
